import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { cn, Price } from '@divlab/divanui';
import { CSSTransition } from 'react-transition-group';

import * as PageProductStore from '@Stores/PageProduct';
import * as Cart from '@Queries/Cart';
import useNavigation from '@Navigation/useNavigation';
import { useRelatedProducts } from '@Queries/RelatedProducts';
import { useDeps } from '@Contexts/DI';
import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import Button from '@UI/Button';
import styles from './StickyBuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';
import type { ConfiguratorData } from '../..';
import type { CartModalInputProduct } from '@Types/Cart';
import type { ProductData } from '@Types/Product';

interface Parameter {
  id: number;
  count: number;
  parameter_id: number;
}
export interface StickyBuyButtonProps extends ButtonProps {
  className?: string;
  visible?: boolean;
  configurator: ConfiguratorData;
}

const StickyBuyButton: FC<StickyBuyButtonProps> = (props) => {
  const { className, visible = false, configurator, ...restProps } = props;
  const { configuration } = configurator;
  const page = PageProductStore.useData();
  const productParameterValues = PageProductStore.getParameterValues();
  const countOfChangedOptions = PageProductStore.getCountOfChangedParameters();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { openPage } = useNavigation();
  const { country, region } = useRequest();
  const isUZB = country === 'UZB';
  const { logger } = useDeps();
  const addToCart = Cart.useAddToCart();
  const { data: relatedProducts } = useRelatedProducts(page.product.id);
  const isNotModify =
    (page.isConfigurator && configuration.parameterValues.length < 1) ||
    (!page.isConfigurator && countOfChangedOptions === 0);
  const isModifiedParameters = !isNotModify || page.isModular || productParameterValues.length > 0;
  const paymentTypeId = -1;

  const parameterValues = useMemo<Parameter[]>(() => {
    if (isNotModify && !page.isModular) return [];
    const params = [];

    if (page.isConfigurator) {
      configuration.parameterValues.forEach((value) => {
        params.push({
          id: value.value,
          count: value.count,
          parameter_id: value.parameterIds[0],
        });
      });
    } else {
      productParameterValues.forEach((value) => {
        params.push(value);
      });
    }

    return params;
  }, [
    page.isConfigurator,
    page.isModular,
    isNotModify,
    configuration.parameterValues,
    productParameterValues,
  ]);

  const product = useMemo<CartModalInputProduct>(() => {
    return {
      image: configuration.image,
      shopProductId: configuration.id,
      parameterValues,
      isModifiedParameters,
      fabricValues: isNotModify
        ? null
        : configuration.parameterValues
            .filter((p) => p.type === 'fabric')
            .map((p) => ({
              fabric_id: p.value,
              parameter_id: p.parameterIds[0],
            })),
      quantity: 1,
    };
  }, [
    configuration.id,
    configuration.image,
    isNotModify,
    configuration.parameterValues,
    isModifiedParameters,
    parameterValues,
  ]);

  const hasInCart = Cart.useHasInCart(product);
  const buttonText = t(hasInCart ? 'ui.in-cart-short' : 'ui.add-to-cart');

  const handleError = useCallback(
    (err: unknown) => {
      logger.log(err);
    },
    [logger],
  );

  const handleClick = useCallback(() => {
    if (hasInCart) {
      openPage({ url: `${region}/order/check` });
      return;
    }

    if (addToCart.isPending || addToCart.isError) {
      return;
    }
    const products: CartModalInputProduct[] = [
      {
        ...product,
        isModular: page.isModular,
        paymentTypeId,
      },
    ];
    const analyticsProducts: ProductData[] = [
      {
        id: configuration.id,
        breadcrumbs: configuration.breadcrumbs,
        name: configuration.name,
        price: configuration.price,
        credit: configuration.credit,
        images: [],
        link: '',
        type: '',
        parameterGroups: [],
        parameterValues: [],
        parameters: [],
        isActive: true,
      },
    ];

    if (configuration.isBasic) {
      const selectedRelatedProducts =
        relatedProducts?.products.filter((pr) => pr.quantity > 0) || [];

      selectedRelatedProducts.forEach((pr) => {
        products.push({
          shopProductId: pr.id,
          quantity: pr.quantity,
          isModular: false,
          paymentTypeId,
        });
        analyticsProducts.push(pr);
      });
    }

    addToCart.mutate(
      {
        products,
        options: { isRelated: false },
        analyticsData: {
          products: analyticsProducts,
          label: 'Карточка товара',
        },
      },
      {
        onError: handleError,
      },
    );
  }, [
    hasInCart,
    addToCart,
    product,
    page.isModular,
    paymentTypeId,
    configuration.id,
    configuration.breadcrumbs,
    configuration.name,
    configuration.price,
    configuration.credit,
    configuration.isBasic,
    handleError,
    openPage,
    region,
    relatedProducts?.products,
  ]);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  return (
    <CSSTransition
      timeout={400}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      unmountOnExit
      in={open}
    >
      <div className={cn(styles.inner, { [styles.UZB]: isUZB }, className)}>
        <Price className={styles.price} price={configuration.price.actual} />
        <Button
          {...restProps}
          wide
          size='l'
          waiting={addToCart.isPending}
          className={cn(styles.buyButton, styles.sizeBig)}
          theme={hasInCart ? 'transparent' : 'primary'}
          onClick={handleClick}
          data-testid='mobile-buy-button'
          aria-label={buttonText}
        >
          {buttonText}
        </Button>
      </div>
    </CSSTransition>
  );
};

export default memo(StickyBuyButton);
